import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Package,
  Plus,
  Minus,
  Clock,
  CheckCircle,
  Bell,
  QrCode,
  Copy,
  History,
  ShoppingBag,
  X,
  RefreshCw,
  Loader2,
  Link as LinkIcon,
  Phone,
  User,
  Euro
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TakeawayOrders = () => {
  const [orders, setOrders] = useState([]);
  const [historyOrders, setHistoryOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [activeTab, setActiveTab] = useState('active');
  
  // New order form state
  const [cart, setCart] = useState([]);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [creating, setCreating] = useState(false);
  
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [ordersRes, categoriesRes, dishesRes, statsRes] = await Promise.all([
        fetch(`${API}/api/takeaway/orders?status=pending`, { headers: getAuthHeaders() }),
        fetch(`${API}/api/categories`, { headers: getAuthHeaders() }),
        fetch(`${API}/api/dishes`, { headers: getAuthHeaders() }),
        fetch(`${API}/api/takeaway/stats`, { headers: getAuthHeaders() })
      ]);
      
      if (ordersRes.ok) {
        const pendingOrders = await ordersRes.json();
        // Also fetch ready orders
        const readyRes = await fetch(`${API}/api/takeaway/orders?status=ready`, { headers: getAuthHeaders() });
        const readyOrders = readyRes.ok ? await readyRes.json() : [];
        setOrders([...pendingOrders, ...readyOrders].sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        ));
      }
      
      if (categoriesRes.ok) setCategories(await categoriesRes.json());
      if (dishesRes.ok) setDishes(await dishesRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/takeaway/orders`, { headers: getAuthHeaders() });
      if (res.ok) {
        const allOrders = await res.json();
        setHistoryOrders(allOrders.filter(o => ['picked_up', 'cancelled'].includes(o.status)));
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    }
  }, [activeTab, fetchHistory]);

  const addToCart = (dish) => {
    const existing = cart.find(item => item.dish_id === dish.id);
    if (existing) {
      setCart(cart.map(item => 
        item.dish_id === dish.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, {
        dish_id: dish.id,
        dish_name: dish.name,
        price: dish.price,
        quantity: 1
      }]);
    }
  };

  const removeFromCart = (dishId) => {
    const existing = cart.find(item => item.dish_id === dishId);
    if (existing && existing.quantity > 1) {
      setCart(cart.map(item =>
        item.dish_id === dishId
          ? { ...item, quantity: item.quantity - 1 }
          : item
      ));
    } else {
      setCart(cart.filter(item => item.dish_id !== dishId));
    }
  };

  const clearCart = () => {
    setCart([]);
    setCustomerName('');
    setCustomerEmail('');
    setCustomerPhone('');
    setOrderNotes('');
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const createOrder = async () => {
    if (cart.length === 0) {
      toast.error('Ajoutez au moins un plat à la commande');
      return;
    }
    
    setCreating(true);
    try {
      const res = await fetch(`${API}/api/takeaway/orders`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          items: cart,
          customer_name: customerName,
          customer_email: customerEmail,
          customer_phone: customerPhone,
          notes: orderNotes
        })
      });
      
      if (res.ok) {
        const newOrder = await res.json();
        toast.success(`Commande ${newOrder.order_number} créée !`);
        setSelectedOrder(newOrder);
        setCreateDialogOpen(false);
        setQrDialogOpen(true);
        clearCart();
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Erreur lors de la création de la commande');
    } finally {
      setCreating(false);
    }
  };

  const markReady = async (orderId) => {
    try {
      const res = await fetch(`${API}/api/takeaway/orders/${orderId}/ready`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const result = await res.json();
        toast.success('Commande marquée comme prête !');
        if (result.notification_sent) {
          toast.info('Notification envoyée au client');
        }
        fetchData();
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const markPickedUp = async (orderId) => {
    try {
      const res = await fetch(`${API}/api/takeaway/orders/${orderId}/picked-up`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        toast.success('Commande clôturée !');
        fetchData();
      } else {
        toast.error('Erreur lors de la clôture');
      }
    } catch (error) {
      toast.error('Erreur lors de la clôture');
    }
  };

  const cancelOrder = async (orderId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler cette commande ?')) return;
    
    try {
      const res = await fetch(`${API}/api/takeaway/orders/${orderId}/cancel`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        toast.success('Commande annulée');
        fetchData();
      } else {
        toast.error('Erreur lors de l\'annulation');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'annulation');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Lien copié !');
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      ready: 'bg-green-100 text-green-800 animate-pulse',
      picked_up: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    const labels = {
      pending: 'En préparation',
      ready: 'Prêt',
      picked_up: 'Récupéré',
      cancelled: 'Annulé'
    };
    return <Badge className={styles[status]}>{labels[status]}</Badge>;
  };

  const filteredDishes = selectedCategory === 'all' 
    ? dishes.filter(d => d.available)
    : dishes.filter(d => d.available && d.category_id === selectedCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="takeaway-orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Commandes à emporter</h1>
          <p className="text-gray-500">Bipper digital - Gérez vos commandes et notifications</p>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} data-testid="refresh-orders-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-orange-500 hover:bg-orange-600" data-testid="new-order-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nouvelle commande
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Créer une commande à emporter</DialogTitle>
              </DialogHeader>
              <div className="grid md:grid-cols-2 gap-4">
                {/* Menu Selection */}
                <div className="space-y-4">
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      size="sm"
                      variant={selectedCategory === 'all' ? 'default' : 'outline'}
                      onClick={() => setSelectedCategory('all')}
                    >
                      Tous
                    </Button>
                    {categories.map(cat => (
                      <Button
                        key={cat.id}
                        size="sm"
                        variant={selectedCategory === cat.id ? 'default' : 'outline'}
                        onClick={() => setSelectedCategory(cat.id)}
                      >
                        {cat.name}
                      </Button>
                    ))}
                  </div>
                  
                  <ScrollArea className="h-[250px] pr-4">
                    <div className="grid gap-2">
                      {filteredDishes.map(dish => (
                        <div
                          key={dish.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                          onClick={() => addToCart(dish)}
                          data-testid={`dish-${dish.id}`}
                        >
                          <div className="flex-1">
                            <p className="font-medium text-sm">{dish.name}</p>
                            <p className="text-orange-600 text-sm">{dish.price.toFixed(2)}€</p>
                          </div>
                          <Button size="sm" variant="ghost">
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
                
                {/* Cart & Customer Info */}
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Nom du client</Label>
                      <Input
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        placeholder="Optionnel"
                        className="h-9"
                        data-testid="customer-name-input"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Téléphone</Label>
                      <Input
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        placeholder="Optionnel"
                        className="h-9"
                        data-testid="customer-phone-input"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">Email du client</Label>
                    <Input
                      type="email"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      placeholder="Pour notification quand c'est prêt"
                      className="h-9"
                      data-testid="customer-email-input"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">Notes</Label>
                    <Textarea
                      value={orderNotes}
                      onChange={(e) => setOrderNotes(e.target.value)}
                      placeholder="Instructions spéciales..."
                      rows={1}
                      className="min-h-[36px]"
                      data-testid="order-notes-input"
                    />
                  </div>
                  
                  <div className="border rounded-lg p-3 space-y-2">
                    <h4 className="font-medium flex items-center gap-2 text-sm">
                      <ShoppingBag className="h-4 w-4" />
                      Panier ({cart.length} articles)
                    </h4>
                    
                    {cart.length === 0 ? (
                      <p className="text-gray-500 text-xs py-2 text-center">
                        Cliquez sur les plats pour les ajouter
                      </p>
                    ) : (
                      <ScrollArea className="h-[100px]">
                        {cart.map(item => (
                          <div key={item.dish_id} className="flex items-center justify-between py-1.5 border-b last:border-0">
                            <div className="flex-1">
                              <p className="text-sm font-medium">{item.dish_name}</p>
                              <p className="text-xs text-gray-500">{item.price.toFixed(2)}€ x {item.quantity}</p>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => removeFromCart(item.dish_id)}>
                                <Minus className="h-3 w-3" />
                              </Button>
                              <span className="w-5 text-center text-sm">{item.quantity}</span>
                              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => addToCart({ id: item.dish_id, name: item.dish_name, price: item.price })}>
                                <Plus className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </ScrollArea>
                    )}
                    
                    <div className="flex justify-between items-center pt-2 border-t font-bold">
                      <span>Total</span>
                      <span className="text-orange-600">{cartTotal.toFixed(2)}€</span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" onClick={clearCart} className="flex-1" size="sm">
                      Vider
                    </Button>
                    <Button 
                      onClick={createOrder} 
                      className="flex-1 bg-orange-500 hover:bg-orange-600"
                      disabled={cart.length === 0 || creating}
                      size="sm"
                      data-testid="create-order-btn"
                    >
                      {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                      Valider
                    </Button>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Clock className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.today.pending}</p>
                  <p className="text-xs text-gray-500">En attente</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Bell className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.today.ready}</p>
                  <p className="text-xs text-gray-500">Prêtes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.today.completed}</p>
                  <p className="text-xs text-gray-500">Récupérées</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Euro className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.today.revenue.toFixed(0)}€</p>
                  <p className="text-xs text-gray-500">CA du jour</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Orders Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="active" className="flex gap-2">
            <Package className="h-4 w-4" />
            En cours ({orders.length})
          </TabsTrigger>
          <TabsTrigger value="history" className="flex gap-2">
            <History className="h-4 w-4" />
            Historique
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="active" className="mt-4">
          {orders.length === 0 ? (
            <Card className="p-8 text-center">
              <Package className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Aucune commande en cours</p>
              <Button 
                variant="link" 
                className="text-orange-500 mt-2"
                onClick={() => setCreateDialogOpen(true)}
              >
                Créer une nouvelle commande
              </Button>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {orders.map(order => (
                <Card key={order.id} className={`${order.status === 'ready' ? 'ring-2 ring-green-500' : ''}`} data-testid={`order-card-${order.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{order.order_number}</CardTitle>
                        <p className="text-xs text-gray-500">
                          {new Date(order.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      {getStatusBadge(order.status)}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {order.customer_name && (
                      <div className="flex items-center gap-2 text-sm">
                        <User className="h-4 w-4 text-gray-400" />
                        <span>{order.customer_name}</span>
                      </div>
                    )}
                    {order.customer_phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <span>{order.customer_phone}</span>
                      </div>
                    )}
                    
                    <div className="border-t pt-2">
                      {order.items.slice(0, 3).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm py-1">
                          <span>{item.quantity}x {item.dish_name}</span>
                          <span>{(item.quantity * item.price).toFixed(2)}€</span>
                        </div>
                      ))}
                      {order.items.length > 3 && (
                        <p className="text-xs text-gray-500">+{order.items.length - 3} autres articles</p>
                      )}
                    </div>
                    
                    <div className="flex justify-between font-bold border-t pt-2">
                      <span>Total</span>
                      <span className="text-orange-600">{order.total.toFixed(2)}€</span>
                    </div>
                    
                    <div className="flex gap-2 pt-2">
                      {order.status === 'pending' && (
                        <>
                          <Button 
                            size="sm" 
                            className="flex-1 bg-green-500 hover:bg-green-600"
                            onClick={() => markReady(order.id)}
                            data-testid={`mark-ready-${order.id}`}
                          >
                            <Bell className="h-4 w-4 mr-1" />
                            Prêt
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setSelectedOrder(order);
                              setQrDialogOpen(true);
                            }}
                          >
                            <QrCode className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      {order.status === 'ready' && (
                        <Button 
                          size="sm" 
                          className="flex-1 bg-blue-500 hover:bg-blue-600"
                          onClick={() => markPickedUp(order.id)}
                          data-testid={`mark-picked-${order.id}`}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Récupéré
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => cancelOrder(order.id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="history" className="mt-4">
          {historyOrders.length === 0 ? (
            <Card className="p-8 text-center">
              <History className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Aucune commande dans l'historique</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {historyOrders.map(order => (
                <Card key={order.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="font-bold">{order.order_number}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(order.created_at).toLocaleDateString('fr-FR')} à{' '}
                          {new Date(order.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      {order.customer_name && (
                        <span className="text-sm text-gray-600">{order.customer_name}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-bold text-orange-600">{order.total.toFixed(2)}€</span>
                      {getStatusBadge(order.status)}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>QR Code & Lien de suivi</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4 text-center">
              <p className="text-lg font-bold">Commande {selectedOrder.order_number}</p>
              
              {selectedOrder.qr_code && (
                <div className="bg-white p-4 rounded-lg inline-block mx-auto">
                  <img 
                    src={`data:image/png;base64,${selectedOrder.qr_code}`} 
                    alt="QR Code"
                    className="w-48 h-48 mx-auto"
                  />
                </div>
              )}
              
              <p className="text-sm text-gray-500">
                Partagez ce QR code ou le lien avec votre client pour qu'il puisse suivre sa commande et recevoir une notification quand elle est prête.
              </p>
              
              <div className="flex gap-2">
                <Input 
                  value={selectedOrder.tracking_url || `https://zotmenu.fr/order/${selectedOrder.tracking_token}`}
                  readOnly
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  onClick={() => copyToClipboard(selectedOrder.tracking_url || `https://zotmenu.fr/order/${selectedOrder.tracking_token}`)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="bg-orange-50 p-3 rounded-lg text-sm text-orange-800">
                <LinkIcon className="h-4 w-4 inline mr-2" />
                Le client pourra activer les notifications sur cette page
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TakeawayOrders;
